import React, { forwardRef, useCallback } from 'react';
import clsx from 'clsx';
import Proptypes from 'prop-types';
import MaterialSwitch from '@material-ui/core/Switch';
import CheckCircleRounded from '@material-ui/icons/CheckCircleRounded';
import CancelRounded from '@material-ui/icons/CancelRounded';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import { makeStyles } from '../../lib/styles';
import Cream from '../Icons/Cream';

const useStyles = makeStyles(
  theme => {
    return {
      root: {
        padding: 0,
        width: 80,
        height: 40,
      },
      creamSwitchBase: {
        padding: 1,
        color: theme.palette.common.white,
        '&:not(.Mui-disabled), &.Mui-disabled': {
          color: theme.palette.common.white,
        },
        '&:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.common.muted,
          opacity: 1,
        },
        '&$checked:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
        },
        '&$checked:hover': {
          backgroundColor: `${theme.palette.primary.main}11`,
        },
        '&$checked': {
          transform: 'translate(100%)',
        },
      },
      defaultSwitchBase: {
        padding: 1,
        color: theme.palette.common.white,
        '&:not(.Mui-disabled), &.Mui-disabled': {
          color: theme.palette.common.white,
        },
        '&:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.common.muted,
          opacity: 1,
        },
        '&$checked:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.primary.main,
          opacity: 1,
        },
        '&$checked:hover': {
          backgroundColor: `${theme.palette.primary.main}11`,
        },
        '&$checked': {
          transform: 'translate(100%)',
        },
      },
      redgreenSwitchBase: {
        padding: 1,
        color: theme.palette.common.white,
        '&:not(.Mui-disabled), &.Mui-disabled': {
          color: theme.palette.common.white,
        },
        backgroundColor: theme.palette.danger.main,
        '&$checked:not(.Mui-disabled)': {
          backgroundColor: theme.palette.success.main,
        },
        '&:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.danger.main,
          opacity: 1,
        },
        '&$checked:not(.Mui-disabled) + $track': {
          backgroundColor: theme.palette.success.main,
          opacity: 1,
        },
        '&$checked': {
          transform: 'translate(100%)',
        },
      },
      checked: {},
      track: {
        borderRadius: 38,
      },
      icon: {
        width: 38,
        height: 38,
      },
      rotate: {
        transform: 'rotate(90deg)',
      },
    };
  },
  { name: 'Toggle' }
);

const Toggle = forwardRef(function Toggle(
  { className, checked, onChange, inputProps, variant, inputRef, ...restProps },
  ref
) {
  const {
    icon,
    rotate,
    redgreenSwitchBase,
    defaultSwitchBase,
    creamSwitchBase,
    ...classes
  } = useStyles({
    variant,
  });
  const handleOnChange = useCallback((event, val) => onChange(val, event), [onChange]);

  let checkedIcon = <CheckCircleRounded className={icon} />;
  let uncheckedIcon = <CancelRounded className={icon} />;
  let switchBase = defaultSwitchBase;

  if (variant === 'redgreen') {
    checkedIcon = <RemoveCircle className={icon} />;
    uncheckedIcon = <RemoveCircle className={clsx([icon, rotate])} />;
    switchBase = redgreenSwitchBase;
  }

  if (variant === 'cream') {
    checkedIcon = <Cream className={icon} checked={checked} />;
    uncheckedIcon = <Cream className={icon} checked={checked} />;
    switchBase = creamSwitchBase;
  }

  return (
    <MaterialSwitch
      {...restProps}
      ref={ref}
      classes={{
        ...classes,
        switchBase,
      }}
      checkedIcon={checkedIcon}
      icon={uncheckedIcon}
      className={clsx([className])}
      checked={checked}
      onChange={handleOnChange}
      inputProps={inputProps}
      inputRef={inputRef}
    />
  );
});

Toggle.propTypes = {
  className: Proptypes.string,
  checked: Proptypes.bool,
  onChange: Proptypes.func.isRequired,
  variant: Proptypes.oneOf(['default', 'redgreen', 'cream']),
  inputProps: Proptypes.shape({
    id: Proptypes.string,
    name: Proptypes.string,
  }),
  inputRef: Proptypes.oneOfType([Proptypes.func, Proptypes.object]),
};

Toggle.defaultProps = {
  className: '',
  checked: false,
  inputProps: null,
  variant: 'default',
  inputRef: null,
};

Toggle.displayName = 'Toggle';

export default Toggle;
